import { X } from 'lucide-react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import api from '../../services/api';

const maskResidentNumber = (residentNumber) => {
  if (!residentNumber) {
    return '';
  }

  const value = String(residentNumber).trim();
  const standardMatch = value.match(/^(\d{6})-?(\d)(\d{6})$/);
  if (standardMatch) {
    return `${standardMatch[1]}-${standardMatch[2]}******`;
  }

  const [front, back] = value.split('-');
  if (back && back.length >= 7) {
    return `${front}-${back[0]}${'*'.repeat(back.length - 1)}`;
  }

  return value;
};

const TenantModal = ({ isOpen, tenant, mode, onClose, onSuccess }) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  // 계약 정보 가져오기 (임차인이 있는 경우)
  const { data: contractData, isLoading: contractLoading, error: contractError } = useQuery(
    ['tenant-contract', tenant?.id],
    () => api.get(`/tenants/${tenant.id}/contract`),
    {
      enabled: !!tenant?.id,
      onSuccess: (data) => {
        console.log('🔍 계약 정보 로드 성공:', data);
      },
      onError: (error) => {
        console.log('🔍 계약 정보 없음 또는 오류:', error);
        console.log('🔍 오류 상세:', error.response?.data);
      }
    }
  );

  // 디버깅 로그
  console.log('🔍 TenantModal 디버깅:');
  console.log('  - isOpen:', isOpen);
  console.log('  - tenant:', tenant);
  console.log('  - tenant?.id:', tenant?.id);
  console.log('  - mode:', mode);
  console.log('  - contractData:', contractData);
  console.log('  - contractLoading:', contractLoading);
  console.log('  - contractError:', contractError);
  console.log('  - API URL:', tenant?.id ? `/tenants/${tenant.id}/contract` : 'N/A');
  console.log('  - 폼 필드들:', {
    building_name: contractData?.building_name,
    unit_number: contractData?.unit_number,
    contract_start_date: contractData?.start_date,
    contract_end_date: contractData?.end_date,
    deposit: contractData?.deposit,
    monthly_rent: contractData?.monthly_rent,
    management_fee: contractData?.maintenance_fee
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      ...(tenant || {}),
      resident_number: isViewMode ? maskResidentNumber(tenant?.resident_number) : (tenant?.resident_number || ''),
      // 계약 관련 정보 초기값
      building_name: '',
      unit_number: '',
      contract_start_date: '',
      contract_end_date: '',
      deposit: '',
      monthly_rent: '',
      management_fee: ''
    }
  });

  // 계약 데이터가 로드되면 폼 값 업데이트
  React.useEffect(() => {
    if (contractData) {
      setValue('building_name', contractData.building_name || '');
      setValue('unit_number', contractData.unit_number || '');
      setValue('contract_start_date', contractData.start_date || '');
      setValue('contract_end_date', contractData.end_date || '');
      setValue('deposit', contractData.deposit || '');
      setValue('monthly_rent', contractData.monthly_rent || '');
      setValue('management_fee', contractData.maintenance_fee || '');
    }
  }, [contractData, setValue]);

  // 임차인 추가
  const createTenantMutation = useMutation(
    (data) => api.post('/tenants', data),
    {
      onSuccess: () => {
        toast.success('임차인이 성공적으로 등록되었습니다.');
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '임차인 등록에 실패했습니다.');
      }
    }
  );

  // 임차인 수정
  const updateTenantMutation = useMutation(
    (data) => api.put(`/tenants/${tenant.id}`, data),
    {
      onSuccess: () => {
        toast.success('임차인 정보가 성공적으로 수정되었습니다.');
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '임차인 수정에 실패했습니다.');
      }
    }
  );

  const onSubmit = (data) => {
    if (isEditMode) {
      updateTenantMutation.mutate(data);
    } else {
      createTenantMutation.mutate(data);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // 모달이 열리지 않았으면 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {isViewMode ? '임차인 상세보기' : isEditMode ? '임차인 수정' : '임차인 추가'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 기본 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">기본 정보</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이름 *
                </label>
                <input
                  type="text"
                  {...register('name', { required: '이름을 입력해주세요.' })}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } ${isViewMode ? 'bg-gray-100' : ''}`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  전화번호 *
                </label>
                <input
                  type="tel"
                  {...register('phone', { required: '전화번호를 입력해주세요.' })}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  } ${isViewMode ? 'bg-gray-100' : ''}`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일 *
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: '이메일을 입력해주세요.',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: '유효한 이메일 주소를 입력해주세요.'
                    }
                  })}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } ${isViewMode ? 'bg-gray-100' : ''}`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  주민등록번호
                </label>
                <input
                  type="text"
                  {...register('resident_number')}
                  disabled={isViewMode}
                  placeholder="000000-0000000"
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  외국인등록번호
                </label>
                <input
                  type="text"
                  {...register('id_number')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>
            </div>

            {/* 추가 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">추가 정보</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  직업
                </label>
                <input
                  type="text"
                  {...register('occupation')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  직장
                </label>
                <input
                  type="text"
                  {...register('workplace')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  직장 전화번호
                </label>
                <input
                  type="tel"
                  {...register('workplace_phone')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비상연락처 이름
                </label>
                <input
                  type="text"
                  {...register('emergency_contact')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비상연락처 전화번호
                </label>
                <input
                  type="tel"
                  {...register('emergency_phone')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>
            </div>
          </div>

          {/* 계약 정보 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">계약 정보</h3>
              {contractLoading && (
                <span className="text-sm text-gray-500">로딩 중...</span>
              )}
              {contractError && (
                <span className="text-sm text-red-500">계약 정보 없음</span>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  건물명
                </label>
                <input
                  type="text"
                  {...register('building_name')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  세대호수
                </label>
                <input
                  type="text"
                  {...register('unit_number')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  계약 시작일
                </label>
                <input
                  type="date"
                  {...register('contract_start_date')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  계약 종료일
                </label>
                <input
                  type="date"
                  {...register('contract_end_date')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  보증금 (원)
                </label>
                <input
                  type="number"
                  {...register('deposit')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                  placeholder="예: 10000000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  월세 (원)
                </label>
                <input
                  type="number"
                  {...register('monthly_rent')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                  placeholder="예: 500000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  관리비 (원)
                </label>
                <input
                  type="number"
                  {...register('management_fee')}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                    isViewMode ? 'bg-gray-100' : ''
                  }`}
                  placeholder="예: 100000"
                />
              </div>
            </div>
          </div>

          {/* 특이사항 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              특이사항
            </label>
            <textarea
              {...register('notes')}
              disabled={isViewMode}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md border-gray-300 ${
                isViewMode ? 'bg-gray-100' : ''
              }`}
              placeholder="특이사항이 있다면 입력해주세요."
            />
          </div>

          {/* 버튼 */}
          {!isViewMode && (
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? '처리 중...' : isEditMode ? '수정' : '등록'}
              </button>
            </div>
          )}

          {isViewMode && (
            <div className="flex justify-end pt-6 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TenantModal;
